// Description of a patient file: sections, fields and lists, shared by the list, the
// file (fiche) and the form. Field names are those stored in MongoDB (server/models/Patient.js).
import { yearsSince } from '../../lib/format';

export const STATUSES = [
  { value: 'Hospitalisé', tone: 'brand' },
  { value: 'Opéré', tone: 'green' },
  { value: 'Non Opéré', tone: 'amber' },
  { value: 'Opéré-Exeat', label: 'Exeat', tone: 'slate' },
  { value: 'Opéré-transféré', label: 'Transféré', tone: 'violet' },
  { value: 'Opéré-décédé', label: 'Décédé', tone: 'dark' },
];
export const statusOf = (value) => STATUSES.find((status) => status.value === value);
export const statusLabel = (value) => (statusOf(value) && (statusOf(value).label || value)) || value;
export const statusTone = (value) => (statusOf(value) ? statusOf(value).tone : 'neutral');

// Units that own patient files (a transfer moves a file from one to another).
export const UNITS = ['Cuomo', 'Ctcv', 'Cardiologie', 'Réanimation'];

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, index) => from + index);

export const BEDS = [
  { label: 'Cuomo', options: range(1, 10).map((n) => ({ value: `Lit ${n}`, label: `Lit ${n}` })) },
  {
    label: 'CTCV',
    options: [
      ...[1, 2, 4].map((n) => ({ value: `CTCV Cab ${n}`, label: `Cab ${n}` })),
      ...range(1, 18).map((n) => ({ value: `CTCV Lit ${n}`, label: `Lit ${n}` })),
      ...range(1, 3).map((n) => ({ value: `CTCV Box ${n}`, label: `Box ${n}` })),
    ],
  },
  {
    label: 'Réanimation CUOMO',
    options: [
      ...range(1, 6).map((n) => ({ value: `Réanimation CUOMO Lit ${n}`, label: `Lit ${n}` })),
      ...range(1, 2).map((n) => ({ value: `Réanimation CUOMO Box ${n}`, label: `Box ${n}` })),
    ],
  },
  { label: 'Réanimation CTCV', options: range(1, 6).map((n) => ({ value: `Réanimation CTCV Lit ${n}`, label: `Lit ${n}` })) },
];

export const NATIONALITIES = [
  {
    label: 'Afrique',
    options: ['Algérie', 'Angola', 'Bénin', 'Botswana', 'Burkina Faso', 'Burundi', "Côte d'Ivoire", 'Djibouti', 'Égypte', 'Érythrée', 'Éthiopie', 'Gabon', 'Gambie', 'Ghana', 'Guinée', 'Guinée-Bissau', 'Kenya', 'Lesotho', 'Libéria', 'Libye', 'Madagascar', 'Malawi', 'Mali', 'Maroc', 'Maurice', 'Mauritanie', 'Mozambique', 'Namibie', 'Niger', 'Nigeria', 'République centrafricaine', 'République démocratique du Congo', 'République du Congo', 'Rwanda', 'Sénégal', 'Soudan', 'Soudan du Sud', 'Tanzanie', 'Tchad', 'Togo', 'Zambie', 'Zimbabwe'],
  },
  { label: 'Amérique', options: ['Argentine', 'Brésil', 'Canada', 'Chili', 'Colombie', 'États-Unis', 'Mexique', 'Venezuela'] },
  { label: 'Asie', options: ['Chine', 'Inde', 'Japon', 'Corée du Sud', 'Indonésie'] },
  { label: 'Europe', options: ['Allemagne', 'France', 'Italie', 'Royaume-Uni', 'Espagne'] },
  { label: 'Océanie', options: ['Australie', 'Nouvelle-Zélande'] },
];

export const PATHOLOGIES = {
  Thoracique: ['Abcès du poumon', 'Abcès pariétal', 'Dilatation des bronches', 'Goître plongeant', 'Greffe aspergillaire', 'Hémothorax', 'Kyste hydatique du poumon', 'Masse intrathoracique', 'Masse pariétale', 'Plaie diaphragmatique', 'Plaie œsophage', 'Plaie thoracique', 'Pleurésie', 'Pneumothorax', 'Poumon détruit post tuberculose', 'Pyothorax', 'Traumatisme du thorax'],
  Vasculaire: ['Plaie vasculaire', 'Thromboses', 'Anévrismes', 'Anévrisme aorte abdominale', 'Gangrène Gazeuse', 'Ischémie aiguë subaigue, critique', 'Faux anévrisme', 'Hématome site ponction', 'Thrombose prothèse', 'Insuffisance veineuse MI', 'Gangrène, nécrose, surinfection', 'Complications FAV', 'AOMI', 'Ischémie', 'IRC stade 5', 'Stenose carotidienne', 'Anévrisme', 'Lymphangiome kystique', 'FAV prothétique'],
  Cardiaque: ['Insuffisance Mitrale', 'Rétrécissement Mitral', 'Insuffisance Aortique', 'Rétrécissement Aortique', 'Insuffisance Tricuspide', 'Insuffisance Pulmonaire', 'Sténose Pulmonaire', 'Plastie fuyante', 'Plastie stenosante', 'Prothèse fuyante', 'Prothèse stenosante', 'Désinsertion prothèse', 'Missmatch prothèse', 'Thrombose prothèse', 'Coronaropathie', 'Endocardite', 'Péricardite Chronique Constrictive', 'Membrane sous-aortique', 'HTAP', 'CIA', 'CIV', 'CAV', 'T4F', 'RVPA', 'PCA', 'Coarctation aorte', 'VDDI', 'Ventricule Unique', 'Transposition des Gros Vaisseaux', 'Malposition des Coronaires', 'Hemtome', 'Épanchement péricardique'],
};

export const DIAGNOSTICS = ['CPA', 'T4F', 'Insuffisance Mitrale', 'Rétrécissement Mitral', 'Insuffisance Aortique', 'Rétrécissement Aortique', 'Insuffisance Tricuspide', 'Insuffisance Pulmonaire', 'Sténose Pulmonaire', 'Plastie fuyante', 'Plastie stenosante', 'Prothèse fuyante', 'Prothèse stenosante', 'Désinsertion prothèse', 'Missmatch prothèse', 'Thrombose prothèse', 'Coronaropathie', 'Endocardite', 'Péricardite Chronique Constrictive', 'Membrane sous-aortique', 'HTAP', 'CIA', 'CIV', 'CAV', 'RVPA', 'PCA', 'Coarctation aorte', 'VDDI', 'Ventricule Unique', 'Transposition des Gros Vaisseaux', 'Malposition des Coronaires', 'Hématome', 'Épanchement péricardique'];

export const SURGEONS = ['Pr Amadou Gabriel CISS', 'Pr Papa Salmane BA', 'Pr Momar Sokna DIOP', 'Dr Papa Amath DIAGNE', 'Dr Papa Ousmane BA', 'Dr Abdou Aziz THIAW', 'Dr Anta Mbaye SALL', 'Dr JOHN', 'Dr ODUYE', 'Dr KAMAL', 'Dr AJADI'];

export const TRANSFER_DEPARTMENTS = ['Réanimation', 'Cardiologie', 'Soins intensifs', 'Pédiatrie', 'Neurologie'];

const currentYear = new Date().getFullYear();
const YEARS = range(1998, currentYear + 1).reverse().map(String);

// Lists stored as text: diagnostics "A,B", pathologies "A, B". Operators are an array.
export const splitList = (value) =>
  (Array.isArray(value) ? value : String(value || '').split(','))
    .map((item) => String(item).trim())
    .filter(Boolean);

// type: text | date | select | segmented | multi | textarea | number
// list: how a "multi" is stored ('array', or the separator of a text field)
export const SECTIONS = [
  {
    id: 'identite',
    title: 'Identité et contact',
    fields: [
      { name: 'dossierNumber', label: 'N° de dossier', required: true, placeholder: 'ex. 01/312' },
      { name: 'nom', label: 'Prénom et nom', required: true },
      { name: 'dateNaissance', label: 'Date de naissance', type: 'date' },
      { name: 'genre', label: 'Sexe', type: 'segmented', options: [{ value: 'Masculin', label: 'Masculin' }, { value: 'Feminin', label: 'Féminin' }] },
      { name: 'groupeSanguin', label: 'Groupe sanguin', type: 'select', options: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'N/A'] },
      { name: 'nationalite', label: 'Nationalité', type: 'select', groups: NATIONALITIES },
      { name: 'profession', label: 'Profession' },
      { name: 'numeroDeTelephone', label: 'Téléphone', inputType: 'tel' },
      { name: 'addressEmail', label: 'Email', inputType: 'email' },
      { name: 'addressDomicile', label: 'Adresse', wide: true },
      { name: 'correspondant', label: 'Tuteur ou responsable', placeholder: 'Nom et téléphone' },
    ],
  },
  {
    id: 'sejour',
    title: 'Séjour',
    fields: [
      { name: 'service', label: 'Service', type: 'select', options: ['CUOMO', 'CTCV', 'CARDIOLOGIE'] },
      { name: 'lit', label: 'Lit', type: 'select', groups: BEDS },
      { name: 'dateEntree', label: 'Date d’entrée', type: 'date' },
      { name: 'statut', label: 'Statut', type: 'select', options: STATUSES.map((status) => ({ value: status.value, label: status.label || status.value })) },
      { name: 'departementTransfert', label: 'Département de transfert', type: 'select', options: TRANSFER_DEPARTMENTS, showIf: (data) => data.statut === 'Opéré-transféré' || Boolean(data.departementTransfert) },
      { name: 'dateSortie', label: 'Date de sortie ou de décès', type: 'date' },
      { name: 'anneeIntervention', label: 'Année d’intervention', type: 'select', options: YEARS },
      { name: 'dateDeces', label: 'Date de décès', type: 'date', recordOnly: true },
      { name: 'dateDesortie', label: 'Date de sortie', type: 'date', recordOnly: true },
    ],
  },
  {
    id: 'diagnostic',
    title: 'Diagnostic',
    fields: [
      { name: 'diagnostic', label: 'Diagnostic(s) d’entrée', type: 'multi', options: DIAGNOSTICS, list: ',', wide: true },
      { name: 'spécialité', label: 'Spécialité', type: 'segmented', options: ['Thoracique', 'Cardiaque', 'Vasculaire'] },
      { name: 'pathologie', label: 'Pathologies', type: 'multi', optionsFrom: (data) => PATHOLOGIES[data['spécialité']] || [], list: ', ', wide: true, hint: 'Choisissez d’abord la spécialité pour voir sa liste.' },
      { name: 'urgence', label: 'Urgence', type: 'segmented', options: [{ value: 'oui', label: 'Oui' }, { value: 'non', label: 'Non' }] },
      { name: 'validation', label: 'Validation du dossier', type: 'segmented', options: ['Validé', 'Non Validé'] },
      { name: 'dateVpa', label: 'Date VPA', type: 'date' },
      { name: 'vpa', label: 'Numéro VPA' },
    ],
  },
  {
    id: 'constantes',
    title: 'Constantes',
    fields: [
      { name: 'ta', label: 'Tension artérielle', placeholder: 'ex. 12/8' },
      { name: 'fc', label: 'Fréquence cardiaque', placeholder: 'bpm' },
      { name: 'spo2', label: 'SpO₂', placeholder: '%' },
      { name: 'temperature', label: 'Température', placeholder: '°C' },
      { name: 'poids', label: 'Poids (kg)', inputType: 'number', step: '0.1' },
      { name: 'taille', label: 'Taille (cm)', inputType: 'number', step: '1' },
      { name: 'imc', label: 'IMC', hint: 'Calculé à partir du poids et de la taille.' },
    ],
  },
  {
    id: 'clinique',
    title: 'Clinique',
    clinical: true,
    fields: [
      { name: 'consultationReason', label: 'Motif de consultation', type: 'textarea', wide: true },
      { name: 'histoire', label: 'Histoire de la maladie', type: 'textarea', wide: true },
      { name: 'antecedents', label: 'Antécédents', type: 'textarea', wide: true },
      { name: 'traitement', label: 'Traitement', type: 'textarea' },
      { name: 'traitementEntree', label: 'Traitement à l’entrée', type: 'textarea' },
    ],
  },
  {
    id: 'examens',
    title: 'Examens',
    clinical: true,
    fields: [
      { name: 'ecrt', label: 'ECRT', type: 'textarea' },
      { name: 'ecg', label: 'ECG', type: 'textarea' },
      { name: 'ett', label: 'ETT et ETO', type: 'textarea' },
      { name: 'tt', label: 'TDM', type: 'textarea' },
      { name: 'coronarographie', label: 'Coronarographie', type: 'textarea' },
      { name: 'biologie', label: 'Biologie', type: 'textarea' },
      { name: 'imagerie', label: 'Imagerie', type: 'textarea' },
      { name: 'autresExamens', label: 'Autres examens', type: 'textarea' },
    ],
  },
  {
    id: 'intervention',
    title: 'Intervention',
    clinical: true,
    fields: [
      { name: 'geste', label: 'Geste', type: 'textarea', wide: true },
      { name: 'operateur', label: 'Opérateur(s)', type: 'multi', options: SURGEONS, list: 'array', wide: true },
      { name: 'cro', label: 'Compte rendu opératoire', type: 'textarea', wide: true },
    ],
  },
  {
    id: 'postop',
    title: 'Post-opératoire',
    clinical: true,
    fields: [
      { name: 'rxthoraxPost', label: 'Radio du thorax', type: 'textarea' },
      { name: 'ecgPost', label: 'ECG', type: 'textarea' },
      { name: 'echoscopiePost', label: 'Échoscopie', type: 'textarea' },
      { name: 'biologiePost', label: 'Biologie', type: 'textarea' },
      { name: 'biologiePostOp', label: 'Biologie (complément)', type: 'textarea' },
      { name: 'gazo', label: 'Gazométrie', type: 'textarea', recordOnly: true },
      { name: 'ecp', label: 'ECP', type: 'textarea', recordOnly: true },
      { name: 'dsr', label: 'DSR', type: 'textarea', recordOnly: true },
    ],
  },
  {
    id: 'suites',
    title: 'Suites et sortie',
    clinical: true,
    fields: [
      { name: 'simpleSuites', label: 'Suites simples', type: 'textarea' },
      { name: 'complications', label: 'Complications', type: 'textarea' },
      { name: 'suite', label: 'Suite', type: 'textarea' },
      { name: 'observation', label: 'Observation', type: 'textarea' },
      { name: 'traitementSortie', label: 'Traitement de sortie', type: 'textarea' },
      { name: 'suivi', label: 'Suivi', type: 'textarea' },
    ],
  },
];

export const FORM_FIELDS = SECTIONS.flatMap((section) => section.fields.filter((field) => !field.recordOnly));

// Older files kept the birth date in `age` (the form labelled it "Age" with a date picker).
const looksLikeDate = (value) => /^\d{4}-\d{2}-\d{2}/.test(String(value || ''));
export const birthDateOf = (patient) => patient.dateNaissance || (looksLikeDate(patient.age) ? patient.age : '');

export const ageLabel = (patient) => {
  const years = yearsSince(birthDateOf(patient));
  if (years !== null) return `${years} an${years > 1 ? 's' : ''}`;
  if (patient.age && !looksLikeDate(patient.age)) return `${patient.age} ans`;
  return '';
};

export const hasValue = (value) => (Array.isArray(value) ? value.filter(Boolean).length > 0 : value !== undefined && value !== null && String(value).trim() !== '');

export const initials = (name = '') =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

// Text searched by the list's search box.
export const searchText = (patient) =>
  [
    patient.nom,
    patient.dossierNumber,
    patient.lit,
    patient.diagnostic,
    patient.pathologie,
    patient.statut,
    patient.validation,
    patient.services,
    patient.service,
    patient.manageur,
    patient.geste,
    patient.consultationReason,
    patient.numeroDeTelephone,
    splitList(patient.operateur).join(' '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
